import * as React from "react"
import {Box, Chip, Grid, Tooltip, Typography} from "@mui/material";
import {AppLink} from "@ui-seeker/app";
import {CheckboxSeeker, IconSeeker} from "@ui-seeker/core";
import {MatchersLazyImage} from "../../@components";
import {FORMAT_DATE_BACK, formatDate, formatPrice, getCurrentDate} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";
import {useProxiedImage} from "../../@hooks";
import {reverseMatcherImageComparatorModalData} from "../@signals";


const currentDateString = formatDate(getCurrentDate(), { format: FORMAT_DATE_BACK })

// Función para determinar el color del badge según el score
const getScoreColor = (distance) => {
	const score = parseFloat(distance)
	if (isNaN(score)) return 'default'
	
	if (score >= 0.9) return 'success'      // Verde - Excelente match
	if (score >= 0.7) return 'primary'      // Azul - Buen match
	if (score >= 0.5) return 'warning'      // Naranja - Match medio
	return 'error'                          // Rojo - Match bajo
}

// Memoised: the selection is a signal the whole page reads, so every change re-rendered the
// complete candidate list (hundreds of cards for a big brand). With `isSelected` as a boolean
// prop only the two cards that change re-render. Same fix as the Raw matcher (869fkbgeq).
function ProductOption({ skuID, skuName, productID, isSelected=false, data, setSelectedSKU, brandOptionsIndex=0, isIAOption=false, groupCount=0, groupProductIDs=[] }) {
	const isAlreadyMapped = data.alreadyMapped === true
	const border = isSelected ? "4px solid" : '1px solid'
	const borderColor = isAlreadyMapped ? "warning.main" : "primary.light"

	const handleCheck = () => setSelectedSKU(isSelected ? null : productID, groupProductIDs)
	const { decodedURL: __thumbnail, imageResourceURL } = useProxiedImage(data.thumbnail)
	const candidateName = `${data.superTitle || ''} ${data.productTitle} ${data.subtitle || ''} ${data.variant || ''}`.trim()

	return <Grid item xs={12}><Grid container border={border} borderColor={borderColor} borderRadius={1} p={.5} pt={0} cursor="pointer" spacing={2} onClick={handleCheck}>
		{ isAlreadyMapped && <Grid item xs={12}>
			<Box 
				display="flex" 
				alignItems="center" 
				gap={1} 
				bgcolor="warning.light" 
				borderRadius={1} 
				p={1}
				sx={{ opacity: 0.9 }}
			>
				<IconSeeker icon="triangle-exclamation" sx={{ color: 'warning.dark', fontSize: "1.2rem" }} />
				<Typography fontWeight="bold" fontSize="0.9rem" color="warning.dark">
					Already Mapped to SKU: {data.skuNameMapped || data.idSkuMapped} ({data.idSkuMapped})
				</Typography>
			</Box>
		</Grid> }
		
		<Grid item xs={2} display="flex" justifyContent="center">
			<CheckboxSeeker label="" checked={isSelected} onChange={handleCheck} sx={{ mx: 0 }} />

			<Box position="relative">
				<MatchersLazyImage
					useSpinner
					src={imageResourceURL}
					sx={{ border: '1px solid', borderColor: 'divider', width: 150, height: 'auto', maxHeight: 200, cursor: 'pointer', borderRadius: 1 }}
					onClick={() => reverseMatcherImageComparatorModalData.value = {
						title: `${data.superTitle} ${data.productTitle} ${data.subtitle}`,
						fullUrlLeft: `${BACKEND_APIS.BACKEND}/sku/image/${skuID}`,
						urlRight: __thumbnail,
						skuName: skuName,
						candidateName: candidateName,
						targetIndex: brandOptionsIndex
					}}
				/>
				{isIAOption && (
					<Box position="absolute" top={-8} right={-8}>
						<Chip
							icon={<IconSeeker icon="sparkles" sx={{ fontSize: "0.7rem !important", color: "inherit !important" }} />}
							label={data.AIDistance ? `${Math.round(parseFloat(data.AIDistance) * 100)}%` : "N/A"}
							color={getScoreColor(data.AIDistance)}
							variant="filled"
							size="small"
							sx={{ fontSize: "0.7rem", height: 20, fontWeight: 'bold' }}
						/>
					</Box>
				)}
				</Box>
		</Grid>

		<Grid item xs={10} display="flex" flexDirection="column" justifyContent="flex-start">
			<Box display="flex" flexDirection="column">
				{ data.superTitle && <Typography color="text.disabled" fontSize="0.95rem">
					{data.superTitle}
				</Typography> }
				<AppLink to={`${BACKEND_APIS.BACKEND}/sku/product-url/${data.siteID}/${productID}/${currentDateString}`} target="_blank">
					<Typography display="flex" alignItems="center" gap={1}>
						{data.productTitle} {data.variant && `${data.variant}`}
						<IconSeeker icon="external-link" sx={{ color: 'text.disabled', fontSize: "0.85rem" }} />
					</Typography>
				</AppLink>
				{ data.subtitle && <Typography color="text.disabled" fontSize="0.95rem">
					{data.subtitle}
				</Typography> }
			</Box>
			<Box display="flex" justifyContent="space-between">
				{groupCount <= 1 && <Typography fontSize="0.85rem">
					ProductID: {data.productID}
				</Typography>}

				<Typography fontSize="0.85rem" sx={groupCount > 1 ? { ml: 'auto' } : undefined}>
					End Date: {formatDate(data.date, { isString: true })}
				</Typography>
			</Box>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Typography fontSize="0.85rem">
					Variant: {data.variant}
				</Typography>

				<Typography fontSize="0.85rem">
					Actual Price: {formatPrice(data.actualPrice, data.currency)}
				</Typography>
			</Box>
			{data.reference && <Typography fontSize="0.85rem" color="text.secondary">
				Reference: {data.reference}
			</Typography>}
			{groupCount > 1 && (
				<Box mt={0.5}>
					<Tooltip
						title={
							<Box>
								<Typography fontSize="0.75rem" fontWeight="bold" mb={0.5}>Product IDs in group:</Typography>
								{groupProductIDs.map(id => (
									<Typography key={id} fontSize="0.7rem" fontFamily="monospace">{id}</Typography>
								))}
							</Box>
						}
						arrow
						placement="bottom-start"
					>
						<Chip
							icon={<IconSeeker icon="layer-group" sx={{ fontSize: "0.7rem !important", color: "inherit !important" }} />}
							label={`${groupCount} products`}
							color="info"
							variant="filled"
							size="small"
							sx={{ fontSize: "0.7rem", height: 20, fontWeight: 'bold', cursor: 'help' }}
						/>
					</Tooltip>
				</Box>
			)}
		</Grid>
	</Grid></Grid>
}

export default React.memo(ProductOption)
